import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ArrowLeft,
  Calendar,
  User,
  MessageSquare,
  Clock,
  History,
  Filter,
  Download,
  FileText,
  ChevronDown,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Eye
} from 'lucide-react';

// --- TYPES ---
interface LeaveRequest {
  id: string;
  created_at: string;
  start_date: string;
  end_date: string;
  type: string;
  reason: string | null;
  status: string;
  rejection_reason?: string | null;
  updated_at?: string | null;
  employee: { full_name: string } | null;
  employee_id: string;
}

interface Employee {
  id: string;
  full_name: string;
}

type TabType = 'pending' | 'history';

const LEAVE_LABELS: Record<string, string> = {
  cp: 'Congés Payés',
  maladie: 'Maladie',
  sans_solde: 'Sans Solde',
  formation: 'Formation',
  autre: 'Autre'
};

const LEAVE_COLORS: Record<string, string> = {
  cp: '#10b981',
  maladie: '#ef4444',
  sans_solde: '#64748b',
  formation: '#8b5cf6',
  autre: '#f59e0b'
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  validated: 'Validé',
  rejected: 'Refusé'
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending: { bg: '#fef3c7', text: '#d97706', border: '#fcd34d' },
  validated: { bg: '#dcfce7', text: '#16a34a', border: '#86efac' },
  rejected: { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' }
};

export default function AdminLeaves() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('pending');

  // Synthèse direction : congés validés ce mois (par le secrétariat)
  const [validatedThisMonth, setValidatedThisMonth] = useState<number>(0);

  // État pour les demandes en attente
  const [pendingLeaves, setPendingLeaves] = useState<LeaveRequest[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);

  // État pour l'historique
  const [historyLeaves, setHistoryLeaves] = useState<LeaveRequest[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Filtres historique
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDateStart, setFilterDateStart] = useState<string>('');
  const [filterDateEnd, setFilterDateEnd] = useState<string>('');

  // --- CHARGEMENT DES DEMANDES EN ATTENTE ---
  const fetchPendingLeaves = useCallback(async () => {
    setLoadingPending(true);
    try {
      const { data, error } = await supabase
        .from('leaves')
        .select('*, employee:employees!employee_id(full_name)')
        .eq('status', 'pending')
        .order('start_date', { ascending: true });

      if (error) throw error;
      if (data) setPendingLeaves(data as unknown as LeaveRequest[]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  // --- CHARGEMENT DE L'HISTORIQUE ---
  const fetchHistoryLeaves = useCallback(async () => {
    setLoadingHistory(true);
    try {
      // Récupérer toutes les demandes non-pending, triées par created_at (plus fiable)
      const { data, error } = await supabase
        .from('leaves')
        .select('*, employee:employees!employee_id(full_name)')
        .neq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      if (data) {
        setHistoryLeaves(data as unknown as LeaveRequest[]);
      }
      setHistoryLoaded(true);
    } catch (e) {
      console.error('Erreur chargement historique:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // --- CHARGEMENT DES EMPLOYÉS POUR FILTRE ---
  const fetchEmployees = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, full_name')
        .order('full_name');

      if (error) throw error;
      if (data) setEmployees(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchPendingLeaves();
    fetchEmployees();
    // Compter les congés validés ce mois (synthèse direction)
    (async () => {
      const now = new Date();
      const monthStart = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
      const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
        .toISOString().slice(0, 10);
      const { count } = await supabase.from('leaves')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'validated')
        .gte('validated_at', monthStart)
        .lt('validated_at', nextMonth);
      setValidatedThisMonth(count ?? 0);
    })();
  }, [fetchPendingLeaves, fetchEmployees]);

  // Charger l'historique quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'history' && !historyLoaded) {
      fetchHistoryLeaves();
    }
  }, [activeTab, historyLoaded, fetchHistoryLeaves]);

  // --- FILTRAGE DE L'HISTORIQUE ---
  const filteredHistory = useMemo(() => {
    return historyLeaves.filter(leave => {
      if (filterEmployee !== 'all' && leave.employee_id !== filterEmployee) return false;
      if (filterType !== 'all' && leave.type !== filterType) return false;
      if (filterStatus !== 'all' && leave.status !== filterStatus) return false;
      if (filterDateStart && leave.start_date < filterDateStart) return false;
      if (filterDateEnd && leave.end_date > filterDateEnd) return false;
      return true;
    });
  }, [historyLeaves, filterEmployee, filterType, filterStatus, filterDateStart, filterDateEnd]);

  // Statistiques
  const stats = useMemo(() => ({
    validated: filteredHistory.filter(l => l.status === 'validated').length,
    rejected: filteredHistory.filter(l => l.status === 'rejected').length
  }), [filteredHistory]);

  // Validation/rejet retirés de cette page (voir useLeaveActions + SecretariatLeaves).
  // AdminLeaves reste en read-only pour la direction (synthèse).

  // --- EXPORT PDF ---
  const exportHistoryPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // En-tête avec dégradé simulé
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Historique des Congés', 14, 22);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(203, 213, 225);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 14, 35);

    // Résumé des filtres
    let yPos = 55;
    const filterParts: string[] = [];

    if (filterEmployee !== 'all') {
      const emp = employees.find(e => e.id === filterEmployee);
      filterParts.push(`Employé: ${emp?.full_name || filterEmployee}`);
    }
    if (filterType !== 'all') filterParts.push(`Type: ${LEAVE_LABELS[filterType] || filterType}`);
    if (filterStatus !== 'all') filterParts.push(`Statut: ${STATUS_LABELS[filterStatus]}`);
    if (filterDateStart) filterParts.push(`Du: ${new Date(filterDateStart).toLocaleDateString('fr-FR')}`);
    if (filterDateEnd) filterParts.push(`Au: ${new Date(filterDateEnd).toLocaleDateString('fr-FR')}`);

    if (filterParts.length > 0) {
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(14, yPos - 5, pageWidth - 28, 15, 2, 2, 'F');
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.text(`Filtres: ${filterParts.join(' | ')}`, 18, yPos + 4);
      yPos += 20;
    }

    // Statistiques
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, yPos - 5, pageWidth - 28, 22, 3, 3, 'F');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('RÉSUMÉ', 20, yPos + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    // Validées
    doc.setFillColor(220, 252, 231);
    doc.roundedRect(60, yPos - 2, 50, 14, 2, 2, 'F');
    doc.setTextColor(22, 163, 74);
    doc.text(`${stats.validated} validée(s)`, 65, yPos + 8);

    // Refusées
    doc.setFillColor(254, 226, 226);
    doc.roundedRect(115, yPos - 2, 50, 14, 2, 2, 'F');
    doc.setTextColor(220, 38, 38);
    doc.text(`${stats.rejected} refusée(s)`, 120, yPos + 8);

    // Total
    doc.setTextColor(100, 116, 139);
    doc.text(`Total: ${filteredHistory.length}`, pageWidth - 45, yPos + 8);

    yPos += 30;

    // Tableau
    const tableData = filteredHistory.map(leave => [
      leave.employee?.full_name || 'N/A',
      LEAVE_LABELS[leave.type] || leave.type,
      new Date(leave.start_date).toLocaleDateString('fr-FR'),
      new Date(leave.end_date).toLocaleDateString('fr-FR'),
      STATUS_LABELS[leave.status] || leave.status,
      leave.updated_at
        ? new Date(leave.updated_at).toLocaleDateString('fr-FR')
        : new Date(leave.created_at).toLocaleDateString('fr-FR'),
      leave.rejection_reason || '-'
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Employé', 'Type', 'Début', 'Fin', 'Statut', 'Traité le', 'Motif refus']],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 24 },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 22, halign: 'center' },
        6: { cellWidth: 'auto' }
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const status = filteredHistory[data.row.index]?.status;
          if (status === 'validated') {
            data.cell.styles.textColor = [22, 163, 74];
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'rejected') {
            data.cell.styles.textColor = [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // Pied de page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${i} / ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`historique_conges_${dateStr}.pdf`);
  };

  // --- RESET FILTRES ---
  const resetFilters = () => {
    setFilterEmployee('all');
    setFilterType('all');
    setFilterStatus('all');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // Rafraîchir l'historique
  const refreshHistory = () => {
    setHistoryLoaded(false);
    fetchHistoryLeaves();
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', maxWidth: '1400px', margin: '0 auto' }}>

      {/* HEADER */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <ArrowLeft size={20} color="#64748b" />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.75rem', color: '#0f172a', fontWeight: '700' }}>
              Suivi des Absences
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' }}>
              Vue direction · validation à la charge du secrétariat
            </p>
          </div>
        </div>

        {/* SYNTHESE DIRECTION — stats du mois pour JLO */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            background: 'white', border: '1px solid #bbf7d0', borderRadius: '14px',
            padding: '12px 18px',
          }}>
            <div style={{ background: '#ecfdf5', padding: '8px', borderRadius: '10px', color: '#10b981', display: 'flex' }}>
              <CheckCircle2 size={18} />
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{validatedThisMonth}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>validés ce mois</div>
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            background: 'white', border: '1px solid #fde68a', borderRadius: '14px',
            padding: '12px 18px',
          }}>
            <div style={{ background: '#fef3c7', padding: '8px', borderRadius: '10px', color: '#d97706', display: 'flex' }}>
              <Clock size={18} />
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{pendingLeaves.length}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>en attente secrétariat</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '16px', marginTop: '-16px', marginBottom: '16px' }}>

        {/* Badge demandes en attente */}
        {pendingLeaves.length > 0 && activeTab === 'history' && (
          <button
            onClick={() => setActiveTab('pending')}
            style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #fcd34d',
              padding: '10px 20px',
              borderRadius: '24px',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#b45309',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(251, 191, 36, 0.3)',
              transition: 'transform 0.2s'
            }}
          >
            <Clock size={16} />
            {pendingLeaves.length} demande(s) en attente
          </button>
        )}
      </div>

      {/* ONGLETS */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '28px',
        background: '#f1f5f9',
        padding: '6px',
        borderRadius: '16px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '12px 28px',
            background: activeTab === 'pending' ? 'white' : 'transparent',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            color: activeTab === 'pending' ? '#0f172a' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.95rem',
            transition: 'all 0.2s',
            boxShadow: activeTab === 'pending' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
          }}
        >
          <Clock size={18} />
          En attente
          {pendingLeaves.length > 0 && (
            <span style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '700',
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {pendingLeaves.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '12px 28px',
            background: activeTab === 'history' ? 'white' : 'transparent',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            color: activeTab === 'history' ? '#0f172a' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.95rem',
            transition: 'all 0.2s',
            boxShadow: activeTab === 'history' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
          }}
        >
          <History size={18} />
          Historique
        </button>
      </div>

      {/* CONTENU ONGLET EN ATTENTE */}
      {activeTab === 'pending' && (
        <>
          {loadingPending ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: '16px' }}>Chargement des demandes...</p>
            </div>
          ) : pendingLeaves.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              borderRadius: '24px',
              border: '2px dashed #e2e8f0'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
              }}>
                <CheckCircle2 size={40} color="#16a34a" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.3rem', color: '#0f172a', fontWeight: '600' }}>
                Tout est à jour !
              </h3>
              <p style={{ margin: 0, fontSize: '1rem', color: '#64748b' }}>
                Aucune demande d'absence en attente de validation.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {pendingLeaves.map(leave => {
                const color = LEAVE_COLORS[leave.type] || '#64748b';
                const label = LEAVE_LABELS[leave.type] || leave.type.toUpperCase();

                return (
                  <div
                    key={leave.id}
                    style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '20px',
                      padding: '28px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '24px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                      transition: 'box-shadow 0.2s'
                    }}
                  >
                    {/* En-tête carte */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                          padding: '14px',
                          borderRadius: '16px',
                          color: '#475569'
                        }}>
                          <User size={28} />
                        </div>
                        <div>
                          <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}>
                            {leave.employee?.full_name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{
                              fontSize: '0.75rem',
                              fontWeight: '700',
                              color: 'white',
                              background: color,
                              padding: '4px 12px',
                              borderRadius: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {label}
                            </span>
                            <span style={{ fontSize: '0.85rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={14} /> Demandé le {new Date(leave.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div style={{
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        padding: '14px 20px',
                        borderRadius: '14px',
                        border: '1px solid #bfdbfe'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Période demandée
                        </div>
                        <div style={{ color: '#1e40af', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1rem' }}>
                          <Calendar size={18} />
                          {new Date(leave.start_date).toLocaleDateString('fr-FR')}
                          <span style={{ color: '#93c5fd' }}>→</span>
                          {new Date(leave.end_date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>

                    {/* Motif si présent */}
                    {leave.reason && (
                      <div style={{
                        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                        border: '1px solid #fde68a',
                        padding: '16px 20px',
                        borderRadius: '14px',
                        display: 'flex',
                        gap: '14px',
                        alignItems: 'flex-start'
                      }}>
                        <MessageSquare size={20} color="#d97706" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#b45309', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Motif de l'employé
                          </div>
                          <div style={{ fontSize: '0.95rem', color: '#92400e', fontStyle: 'italic', lineHeight: '1.5' }}>
                            "{leave.reason}"
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Vue direction : read-only. La validation est désormais
                        assurée uniquement par le secrétariat. */}
                    <div style={{
                      padding: '14px 18px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                      <Eye size={18} strokeWidth={2} />
                      <span>Vue direction — validation à la charge du secrétariat</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* CONTENU ONGLET HISTORIQUE */}
      {activeTab === 'history' && (
        <>
          {/* Barre de filtres */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
          }}>
            {/* En-tête filtres */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontWeight: '700', fontSize: '0.9rem' }}>
                <Filter size={18} />
                FILTRES
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={refreshHistory}
                  disabled={loadingHistory}
                  style={{
                    padding: '10px 16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#64748b',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <RefreshCw size={14} style={{ animation: loadingHistory ? 'spin 1s linear infinite' : 'none' }} />
                  Actualiser
                </button>
                <button
                  onClick={resetFilters}
                  style={{
                    padding: '10px 16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#64748b',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  Réinitialiser
                </button>
                <button
                  onClick={exportHistoryPDF}
                  disabled={filteredHistory.length === 0}
                  style={{
                    padding: '10px 20px',
                    background: filteredHistory.length === 0 ? '#e2e8f0' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: filteredHistory.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    color: 'white',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: filteredHistory.length > 0 ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <Download size={16} />
                  Exporter PDF
                </button>
              </div>
            </div>

            {/* Ligne de filtres */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#f8fafc',
              borderRadius: '14px'
            }}>
              {/* Filtre Employé */}
              <div style={{ position: 'relative' }}>
                <select
                  value={filterEmployee}
                  onChange={(e) => setFilterEmployee(e.target.value)}
                  style={{
                    padding: '10px 36px 10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    fontSize: '0.9rem',
                    color: '#1e293b',
                    cursor: 'pointer',
                    minWidth: '160px',
                    fontWeight: '500',
                    appearance: 'none'
                  }}
                >
                  <option value="all">Tous les employés</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                  ))}
                </select>
                <ChevronDown size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }} />
              </div>

              {/* Filtre Type */}
              <div style={{ position: 'relative' }}>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{
                    padding: '10px 36px 10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    fontSize: '0.9rem',
                    color: '#1e293b',
                    cursor: 'pointer',
                    minWidth: '150px',
                    fontWeight: '500',
                    appearance: 'none'
                  }}
                >
                  <option value="all">Tous les types</option>
                  {Object.entries(LEAVE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <ChevronDown size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }} />
              </div>

              {/* Filtre Statut */}
              <div style={{ position: 'relative' }}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    padding: '10px 36px 10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    fontSize: '0.9rem',
                    color: '#1e293b',
                    cursor: 'pointer',
                    minWidth: '140px',
                    fontWeight: '500',
                    appearance: 'none'
                  }}
                >
                  <option value="all">Tous les statuts</option>
                  <option value="validated">Validé</option>
                  <option value="rejected">Refusé</option>
                </select>
                <ChevronDown size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }} />
              </div>

              {/* Séparateur */}
              <div style={{ width: '1px', height: '32px', background: '#e2e8f0' }} />

              {/* Filtre Date Début */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Du</span>
                <input
                  type="date"
                  value={filterDateStart}
                  onChange={(e) => setFilterDateStart(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    fontSize: '0.9rem',
                    color: '#1e293b',
                    fontWeight: '500'
                  }}
                />
              </div>

              {/* Filtre Date Fin */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Au</span>
                <input
                  type="date"
                  value={filterDateEnd}
                  onChange={(e) => setFilterDateEnd(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    fontSize: '0.9rem',
                    color: '#1e293b',
                    fontWeight: '500'
                  }}
                />
              </div>
            </div>

            {/* Statistiques */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #f1f5f9',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                <strong style={{ color: '#0f172a' }}>{filteredHistory.length}</strong> résultat(s) sur <strong>{historyLeaves.length}</strong> demande(s) traitée(s)
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{stats.validated} validée(s)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444' }} />
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{stats.rejected} refusée(s)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Liste historique */}
          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: '16px' }}>Chargement de l'historique...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              borderRadius: '24px',
              border: '2px dashed #e2e8f0'
            }}>
              <div style={{
                background: '#f1f5f9',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <FileText size={40} color="#94a3b8" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.3rem', color: '#0f172a', fontWeight: '600' }}>
                Aucun résultat
              </h3>
              <p style={{ margin: 0, fontSize: '1rem', color: '#64748b' }}>
                Modifiez vos filtres pour voir d'autres résultats.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredHistory.map(leave => {
                const color = LEAVE_COLORS[leave.type] || '#64748b';
                const label = LEAVE_LABELS[leave.type] || leave.type.toUpperCase();
                const statusStyle = STATUS_COLORS[leave.status] || STATUS_COLORS.pending;
                const isValidated = leave.status === 'validated';

                return (
                  <div
                    key={leave.id}
                    style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '16px',
                      padding: '20px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '20px',
                      flexWrap: 'wrap',
                      borderLeft: `4px solid ${isValidated ? '#22c55e' : '#ef4444'}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'box-shadow 0.2s'
                    }}
                  >
                    {/* Info principale */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1', minWidth: '280px' }}>
                      <div style={{
                        background: isValidated ? '#dcfce7' : '#fee2e2',
                        padding: '12px',
                        borderRadius: '12px',
                        color: isValidated ? '#16a34a' : '#dc2626'
                      }}>
                        {isValidated ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#0f172a', marginBottom: '6px', fontSize: '1.05rem' }}>
                          {leave.employee?.full_name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '0.7rem',
                            fontWeight: '700',
                            color: 'white',
                            background: color,
                            padding: '3px 10px',
                            borderRadius: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}>
                            {label}
                          </span>
                          <span style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: statusStyle.text,
                            background: statusStyle.bg,
                            border: `1px solid ${statusStyle.border}`,
                            padding: '3px 10px',
                            borderRadius: '6px'
                          }}>
                            {STATUS_LABELS[leave.status]}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      color: '#475569',
                      fontSize: '0.95rem',
                      background: '#f8fafc',
                      padding: '10px 16px',
                      borderRadius: '10px',
                      fontWeight: '500'
                    }}>
                      <Calendar size={16} color="#3b82f6" />
                      {new Date(leave.start_date).toLocaleDateString('fr-FR')}
                      <span style={{ color: '#cbd5e1' }}>→</span>
                      {new Date(leave.end_date).toLocaleDateString('fr-FR')}
                    </div>

                    {/* Date de traitement */}
                    <div style={{ fontSize: '0.85rem', color: '#94a3b8', minWidth: '130px', textAlign: 'right' }}>
                      Traité le<br />
                      <strong style={{ color: '#64748b' }}>
                        {leave.updated_at
                          ? new Date(leave.updated_at).toLocaleDateString('fr-FR')
                          : new Date(leave.created_at).toLocaleDateString('fr-FR')}
                      </strong>
                    </div>

                    {/* Motif de refus si présent */}
                    {leave.rejection_reason && (
                      <div style={{
                        width: '100%',
                        marginTop: '8px',
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                        border: '1px solid #fecaca',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        color: '#b91c1c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <MessageSquare size={16} style={{ flexShrink: 0 }} />
                        <span><strong>Motif du refus :</strong> {leave.rejection_reason}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* CSS pour l'animation de spin */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
